import React, { useContext, useEffect, useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GenericModal from '../../../components/modal/GenericModal';
import RubricaUpload from './RubricaUpload';
import { downloadFile } from '../../../consumers/backendConsumer';
import { AuthContext } from '../../../hooks/context/authContext';
import { useGetRequest } from '../../../hooks/useGetRequest/useGetRequest';
import { usePostRequest } from '../../../hooks/usePostRequest/usePostRequest';
import { EXTENSIONES_VALIDAS_DE_ARCHIVOS } from '../../../constants/hardCodedConstants';
import { getIdEmpleadoSesion } from '../utils/sesion';
import { esEvaluadorClaseModelo } from '../utils/claseModelo';
import {
    actualizarClaseModeloAPI,
    listarRubricasClaseModeloAPI,
    listarRubricasEvaluadorClaseModeloAPI,
    guardarRubricaEvaluadorClaseModeloAPI,
} from '../../../consumers/backendApisUrls';

const ESTADO_ENVIADO = 18;
const valor = (...valores) => valores.find(v => v !== undefined && v !== null && v !== '') ?? '';
const data = response => response?.data?.Data ?? response?.data?.data ?? response?.data ?? [];

const ResultadoClaseModelo = ({ toggleModal, refreshPage, postulante = {} }) => {
    const { state } = useContext(AuthContext);
    // Evalua quien es el evaluador asignado o su suplente vigente, no cualquier Director (ver
    // utils/claseModelo.js). El servidor valida lo mismo al enviar la evaluacion.
    const puedeEvaluar = esEvaluadorClaseModelo(postulante, getIdEmpleadoSesion(state));

    const [executeGet] = useGetRequest();
    const [executePost] = usePostRequest();
    const [comentario, setComentario] = useState('');
    const [rubricasPlantilla, setRubricasPlantilla] = useState([]);
    const [rubricasEvaluador, setRubricasEvaluador] = useState([]);
    const [subiendo, setSubiendo] = useState(false);
    const [enviando, setEnviando] = useState(false);

    const idPostulante = Number(valor(postulante.idPostulante, postulante.IdPostulante, 0));
    const idClaseModelo = Number(valor(postulante.idClaseModelo, postulante.IdClaseModelo, 0));
    const nombre = valor(postulante.nombrePostulante, postulante.NombrePostulante, '-');
    const dni = valor(postulante.dni, postulante.Dni, postulante.numeroDocumento, '-');
    const puesto = valor(postulante.desCargo, postulante.DesCargo, postulante.puestoActual, '-');
    const motivoObservado = valor(postulante.motivoObservado, postulante.MotivoObservado, '');

    const cargarRubricas = async () => {
        if (!puedeEvaluar || !idPostulante) return;
        // Plantilla original subida por GDH
        const responsePlantilla = await executeGet(listarRubricasClaseModeloAPI(idPostulante));
        const rowsPlantilla = data(responsePlantilla);

        setRubricasPlantilla(Array.isArray(rowsPlantilla) ? rowsPlantilla : []);

        // Archivo trabajado y subido por el evaluador
        const responseEvaluador = await executeGet(listarRubricasEvaluadorClaseModeloAPI(idPostulante));
        const rowsEvaluador = data(responseEvaluador);

        setRubricasEvaluador(Array.isArray(rowsEvaluador) ? rowsEvaluador : []);
    };

    useEffect(() => {
        cargarRubricas();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [idPostulante, puedeEvaluar]);

    const guardarRubrica = async file => {
        if (!puedeEvaluar) {
            toast.warning('No tienes permisos para evaluar la clase modelo.');
            return;
        }
        if (!file || !file.Path || !file.FileName || !idPostulante)
            return;
        setSubiendo(true);
        try {
            await executePost(guardarRubricaEvaluadorClaseModeloAPI(idPostulante), { File: file });
            await cargarRubricas();
            toast.success('Rúbrica cargada correctamente.');
        } finally {
            setSubiendo(false);
        }
    };

    const descargar = archivo => {
        downloadFile(state.token, archivo.Path ?? archivo.path, archivo.OriginalFileName ?? archivo.originalFileName);
    };

    const enviar = async () => {
        if (!puedeEvaluar) {
            toast.warning('No tienes permisos para evaluar la clase modelo.');
            return;
        }
        if (!idPostulante || !idClaseModelo) {
            toast.error('No se encontró la programación de Clase Modelo a actualizar.');
            return;
        }
        if (!rubricasEvaluador.length) {
            toast.warning('Debes cargar la rúbrica de evaluación antes de enviar.');
            return;
        }

        setEnviando(true);
        try {
            const response = await executePost(actualizarClaseModeloAPI(), {
                idClaseModelo: Number(idClaseModelo),
                idPostulante,
                comentarioEvaluador: comentario.trim() || null,
                motivoObservado: null,
                idEstadoPostulacion: ESTADO_ENVIADO,
            });
            if (response?.response?.status >= 400) {
                toast.error(response?.response?.data?.Message ?? response?.response?.data?.message ?? 'No se pudo enviar la evaluación.');
                return;
            }
            toast.success('Evaluación enviada correctamente.');
            refreshPage && refreshPage();
            toggleModal && toggleModal();
        } finally {
            setEnviando(false);
        }
    };

    const ultimaRubricaPlantilla = rubricasPlantilla[0];
    const ultimaRubricaEvaluador = rubricasEvaluador[0];

    return (
        <GenericModal showModal={toggleModal} titulo="Resultado Clase modelo Líder" width="900px">
            <Container>
                {!puedeEvaluar ? (
                    <div className="text-center py-5">Esta vista corresponde al evaluador asignado a la Clase Modelo (o a su suplente).</div>
                ) : (
                    <>
                        <CampaignBar>{valor(postulante.nombreConvocatoria, 'Nombre campaña')} - Resultado Clase modelo Líder</CampaignBar>

                        <Grid>
                            <Field><label>Nombre del candidato</label><ReadOnly>{nombre}</ReadOnly></Field>
                            <Field><label>DNI</label><ReadOnly>{dni}</ReadOnly></Field>
                            <Field><label>Puesto actual</label><ReadOnly>{puesto}</ReadOnly></Field>
                        </Grid>

                        <Divider />
                        <SectionTitle>Datos de programación</SectionTitle>
                        <Grid>
                            <Field><label>Sede evaluadora</label><ReadOnly>{valor(postulante.desSedeEvaluadora, postulante.DesSedeEvaluadora, postulante.idSedeEvaluadora, '-')}</ReadOnly></Field>
                            <Field><label>Responsable evaluador</label><ReadOnly>{valor(postulante.nombreEmpleadoEvaluador, postulante.NombreEmpleadoEvaluador, '-')}</ReadOnly></Field>
                            <Field><label>Sede de origen</label><ReadOnly>{valor(postulante.desSedeOrigen, postulante.DesSedeOrigen, postulante.idSedeOrigen, '-')}</ReadOnly></Field>
                            <Field><label>Fecha límite</label><ReadOnly>{valor(postulante.fechaLimiteClaseModelo, postulante.FechaLimiteClaseModelo, '-')}</ReadOnly></Field>
                        </Grid>

                        {motivoObservado &&
                            <Observation>
                                <strong>Observación de GDH:</strong> {motivoObservado}
                            </Observation>
                        }

                        <Divider />
                        <SectionTitle>Rúbrica de evaluación</SectionTitle>

                        <RubricaUpload
                            id="clase-modelo-rubrica-evaluador"
                            layout="column"
                            archivos={[
                                ultimaRubricaPlantilla && { archivo: ultimaRubricaPlantilla, etiqueta: 'Plantilla GDH' },
                                ultimaRubricaEvaluador && { archivo: ultimaRubricaEvaluador, etiqueta: 'Rúbrica del evaluador' },
                            ].filter(Boolean)}
                            onDescargar={descargar}
                            uploadFileToS3={guardarRubrica}
                            textButton="Subir mi rúbrica"
                            subiendoArchivoPublicState={subiendo}
                            extensionesValidas={["pdf", "doc", "docx", "xls", "xlsx"]}
                            resetInitialFile={false}
                            setSubiendoArchivoPublicState={setSubiendo}
                            showToastSuccess={false}                 
                        />

                        <label className="mt-3">Comentarios (opcional)</label>
                        <textarea className="form-control" rows="4" value={comentario}
                                  onChange={e => setComentario(e.target.value)} disabled={enviando} />

                        <Actions>
                            <button type="button"
                                    className="generic-button-buscador generic-button-hover-verde green-innova-background"
                                    disabled={enviando || subiendo}
                                    onClick={enviar}>
                                Enviar
                                {enviando && <FontAwesomeIcon icon="spinner" className="spinner ml-2" />}
                            </button>
                        </Actions>
                    </>
                )}
            </Container>
        </GenericModal>
    );
};

export default ResultadoClaseModelo;

const Container = styled.div`padding: 0 16px 18px;`;
const CampaignBar = styled.div`background: #0645ff; color: #fff; padding: 10px 12px; font-weight: 600; margin-bottom: 24px;`;
const Divider = styled.div`border-top: 2px solid #0645ff; margin: 18px 0;`;
const SectionTitle = styled.div`display: inline-block; background: #0645ff; color: #fff; padding: 7px 12px; font-weight: 600; margin-bottom: 14px;`;
const Grid = styled.div`display: grid; grid-template-columns: repeat(3, 1fr); gap: 16px 24px; @media (max-width: 760px) { grid-template-columns: 1fr; }`;
const Field = styled.div`label { display: block; font-size: 12px; margin-bottom: 4px; }`;
const ReadOnly = styled.div`background: #f1f1f1; min-height: 32px; padding: 7px 9px;`;
const Observation = styled.div`margin-top: 18px; padding: 12px; background: #fff3cd; border: 1px solid #ffe69c;`;
const Actions = styled.div`display: flex; justify-content: flex-end; margin-top: 18px; button { min-width: 150px; }`;